import { ArrowIcon } from "../history/components";
import { AvatarIcon } from "../statistics/components/AvatarIcon";
import { clsx } from "clsx";
import { IUser } from "../types";

import { useState, useRef, useEffect } from "react";

type IUserDropdownProps = {
  mini?: boolean;
  primary?: boolean;
  avatar?: boolean;
  users?: IUser[];
  currentUser?: IUser;
  onChange?: (user: IUser) => void;
};

export const UserDropdown: React.FC<IUserDropdownProps> = ({
  users,
  currentUser,
  avatar,
  primary,
  mini,
  onChange,
}) => {
  const [isShow, setIsShow] = useState<boolean>(false);
  const selectRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current &&
        event.target instanceof Node &&
        !selectRef.current.contains(event.target)
      ) {
        setIsShow(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isShow]);

  useEffect(() => {
    if (isShow && listRef.current) {
      listRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [isShow]);

  const onChangeUser = (user: IUser) => {
    onChange?.(user);
    setIsShow(false);
  };

  return (
    <div
      className={clsx("flex items-center justify-end relative duration-500", {
        "text-white": !primary,
        "text-primary": primary,
      })}
      ref={selectRef}
    >
      <span
        className="flex items-center"
        onClick={() => setIsShow((prev) => !prev)}
      >
        {avatar && <AvatarIcon />}
        <span
          className={clsx(
            "mx-1 inline-block overflow-hidden text-ellipsis whitespace-nowrap",
            {
              "max-w-[40px]": mini,
              "max-w-[100px]": !mini,
            }
          )}
        >
          {currentUser?.name}
        </span>
        <span
          className={clsx("transition-transform duration-500", {
            "-rotate-180": isShow,
          })}
        >
          <ArrowIcon />
        </span>
      </span>

      <div
        ref={listRef}
        className={clsx(
          "absolute  rounded-lg overflow-y-auto z-[2] transition-[max-height] duration-500 ease-out custom-scrollbar",
          {
            "max-w-[100px]": mini,
            "max-w-[114px]": !mini,
            "top-[45px] right-[0px]": avatar,
            "top-[26px] right-0": !avatar,
            "bg-white text-[#333] dark:text-white80 dark:bg-black333": !primary,
            "bg-primary text-white": primary,
            "max-h-[210px]": isShow,
            "max-h-0": !isShow,
          }
        )}
      >
        {users?.map((user, index) => (
          <div
            key={index}
            className={clsx(
              "px-[8px] flex items-center border-b border-solid border-[#d9e2ec]",
              {
                "py-[12px] justify-center": !avatar,
                "py-3 justify-start dark:border-gray999": avatar,
              }
            )}
            onClick={() => onChangeUser(user)}
          >
            {avatar && (
              <span className="inline-block flex-shrink-0">
                <AvatarIcon />
              </span>
            )}
            <span
              className={clsx(
                "ml-[4px] overflow-hidden text-ellipsis whitespace-nowrap"
              )}
            >
              {user.name}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
